import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { formatDate } from '../../util/helpers/dateHelpers';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import Button from '../Button/Button';
import { uTextEllipsis } from '../../assets/styles/utility';
import {
  CREATED_ON_FORMAT,
  getDestinationLabel,
  getSourceLabel,
  ORDER_STATUS,
  ORDER_STATUS_TYPE,
  ORDER_STATUS_TYPES_ICONS,
  ORDER_TYPES_LABELS,
  ORDER_TYPES_WITH_DESTINATION,
  ORDER_TYPES_WITH_SOURCE,
  totalPrice,
} from '../../util/helpers/legacyOrderHelper';
import Paragraph from '../Paragraph/Paragraph';
import LegacyOrderDetailsModal from '../Modals/LegacyOrders/LegacyOrderDetailsModal';
import ProductImage from '../LegacyOrder/ProductImage';

export const LegacyOrderHistoryRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  border-radius: ${variables.borderRadius.borderRadius};
  flex-direction: column;

  box-shadow: ${variables.shadow.boxShadow};
  margin-bottom: ${pxToRem(12)};
`;

export const LegacyOrderHistoryRowHeader = styled.div`
  display: flex;
  flex-direction: row;
  grid-template-columns: auto;
  justify-content: space-between;
  width: 100%;
  background-color: ${themeColors.colorBackgroundSecondary};
  padding: ${pxToRem(15)};
`;

export const LegacyOrderHistoryRowDate = styled.div`
  font-weigt: bold;
  font-weight: 600;
  font-size: ${pxToRem(16)};
`;

export const LegacyOrderHistoryRowCode = styled.div`
  font-weight: 400;
  font-size: ${pxToRem(16)};
  margin-top: 10px;
  display: flex;
  align-items: center;

  svg {
    width: 16px;
    height: 16px;
    margin-right: 10px;

    ${({ orderStateType }) =>
      orderStateType === ORDER_STATUS_TYPE.COMPLETED ||
      orderStateType === ORDER_STATUS_TYPE.PAYMENT_ISSUED ||
      orderStateType === ORDER_STATUS_TYPE.FULL_TRACKING_RECEIVED
        ? `color: ${themeColors.colorSuccess}`
        : `color: ${themeColors.colorError}}`}
`;

export const LegacyOrderHistoryHeaderDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-template-columns: calc(1rem * var(--legacy-scale, 1));
`;

export const LegacyOrderHistoryHeaderDataLeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-template-columns: calc(1rem * var(--legacy-scale, 1));
  align-items: flex-end;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin-top: ${pxToRemMd(10)};
  }
`;

export const LegacyOrderHistoryHeaderDataHalfContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-template-columns: calc(1rem * var(--legacy-scale, 1));
  width: 50%;
`;

export const LegacyOrderHistoryRowOrderTotalLabel = styled.div`
  font-weigt: bold;
  font-weight: 600;
  font-size: ${pxToRem(16)};
  justify-content: left;
`;

export const LegacyOrderHistoryRowOrderTotal = styled.div`
  font-weight: 400;
  font-size: ${pxToRem(16)};
  margin-top: 10px;
  justify-content: left;
`;

export const LegacyOrderHistoryRowDelivery = styled.div`
  display: flex;
  flex-direction: row;
  grid-template-columns: 10px;
  justify-content: space-between;
  width: 100%;
  background-color: ${themeColors.colorBackgroundPrimary};
  padding: ${pxToRem(15)};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: column;
    margin-top: ${pxToRemMd(10)};
  }
`;

export const LegacyOrderHistoryRowOrderType = styled.div`
  font-weigt: bold;
  font-weight: 600;
  font-size: ${pxToRem(24)};
`;

export const LegacyOrderHistoryRowOrdeDestinationLabel = styled.div`
  font-weigt: bold;
  font-weight: 600;
  font-size: ${pxToRem(16)};
  justify-content: right;
`;

export const LegacyOrderHistoryRowOrdeDestination = styled.div`
  font-weight: 400;
  font-size: ${pxToRem(16)};
  margin-top: 10px;
  justify-content: right;
`;

export const LegacyOrderHistoryRowOrderItem = styled.div`
  font-weight: 400;
  font-size: ${pxToRem(16)};
  margin-top: 10px;
  justify-content: left;
  width: 100%;
  padding: ${pxToRem(15)} ${pxToRem(15)} ${pxToRem(10)} ${pxToRem(15)};
  border-top: 1px solid ${themeColors.colorBorderPrimary};
`;

export const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  svg {
    color: ${themeColors.colorShippmentPending};
  }
`;

export const StatusIconWrap = styled.div`
  margin-right: ${pxToRem(8)};
  &,
  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    color: ${themeColors.colorPrimary};
    fill: ${themeColors.colorPrimary};
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    &,
    svg {
      width: ${pxToRemMd(16)};
      height: ${pxToRemMd(16)};
    }
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    margin-right: 0;
  }
`;

export const StatusLabel = styled.p`
  ${uTextEllipsis};
  min-width: 0;
  color: ${themeColors.colorTextSecondary};
  font-weight: 500;
`;

export const LegacyOrderHistoryRowFooter = styled.div`
  background-color: ${themeColors.colorBackgroundSecondary};
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  width: 100%;
  padding: ${pxToRem(15)};
`;

const LegacyOrderHistoryRow = ({ legacyOrder }) => {
  const { t } = useTranslation();
  const [openOrderDetails, setOpenOrderDetails] = useState(false);

  if (!legacyOrder) {
    return null;
  }

  return (
    <LegacyOrderHistoryRowContainer>
      <LegacyOrderHistoryRowHeader>
        <LegacyOrderHistoryHeaderDataContainer>
          <LegacyOrderHistoryRowDate>
            {formatDate(legacyOrder.CreatedOn, CREATED_ON_FORMAT)}
          </LegacyOrderHistoryRowDate>
          <LegacyOrderHistoryRowCode>
            {legacyOrder.OrderCode}
          </LegacyOrderHistoryRowCode>
        </LegacyOrderHistoryHeaderDataContainer>
        <LegacyOrderHistoryHeaderDataLeftContainer>
          <LegacyOrderHistoryRowOrderTotalLabel>
            {t('account.legacyOrderHistory.tableRow.orderTotal')}
          </LegacyOrderHistoryRowOrderTotalLabel>

          <LegacyOrderHistoryRowOrderTotal>
            {formatMoneyNumeral(
              totalPrice(
                legacyOrder.Total,
                legacyOrder.VaultShipping,
                legacyOrder.WireFee,
                legacyOrder.OrderType,
              ),
            )}
          </LegacyOrderHistoryRowOrderTotal>
        </LegacyOrderHistoryHeaderDataLeftContainer>
      </LegacyOrderHistoryRowHeader>
      <LegacyOrderHistoryRowDelivery>
        <LegacyOrderHistoryHeaderDataContainer>
          <LegacyOrderHistoryRowOrderType>
            {ORDER_TYPES_LABELS[legacyOrder.OrderType]}
          </LegacyOrderHistoryRowOrderType>
          <LegacyOrderHistoryRowCode
            orderStateType={legacyOrder.OrderStateType}
          >
            {ORDER_STATUS_TYPES_ICONS[legacyOrder.OrderStateType]}
            <Paragraph>{ORDER_STATUS[legacyOrder.OrderStateType]}</Paragraph>
          </LegacyOrderHistoryRowCode>
        </LegacyOrderHistoryHeaderDataContainer>
        {ORDER_TYPES_WITH_SOURCE.includes(legacyOrder.OrderType) &&
          !!legacyOrder.DeliveryCity && (
            <LegacyOrderHistoryHeaderDataHalfContainer>
              <LegacyOrderHistoryRowOrdeDestinationLabel>
                {t('account.legacyOrderHistory.tableRow.source')}
              </LegacyOrderHistoryRowOrdeDestinationLabel>
              <LegacyOrderHistoryRowOrdeDestination>
                {getSourceLabel({
                  orderType: legacyOrder.OrderType,
                  storageLocation: legacyOrder.StorageLocation ?? '',
                  storageType: legacyOrder.StorageType ?? '',
                  deliveryName: legacyOrder.DeliveryName ?? '',
                  addressLine1: legacyOrder.DeliveryStreet1 ?? '',
                  city: legacyOrder.DeliveryCity ?? '',
                  provinceCaption:
                    legacyOrder.DeliveryStateProvinceCaption ?? '',
                  postalCode: legacyOrder.DeliveryPostalCode ?? '',
                  orderItems: legacyOrder.OrderItems ?? '',
                })}
              </LegacyOrderHistoryRowOrdeDestination>
            </LegacyOrderHistoryHeaderDataHalfContainer>
          )}
        {ORDER_TYPES_WITH_DESTINATION.includes(legacyOrder.OrderType) &&
          !!legacyOrder.DeliveryCity && (
            <LegacyOrderHistoryHeaderDataHalfContainer>
              <LegacyOrderHistoryRowOrdeDestinationLabel>
                {t('account.legacyOrderHistory.tableRow.destination')}
              </LegacyOrderHistoryRowOrdeDestinationLabel>
              <LegacyOrderHistoryRowOrdeDestination>
                {getDestinationLabel({
                  orderType: legacyOrder.OrderType,
                  storageLocation: legacyOrder.StorageLocation ?? '',
                  storageType: legacyOrder.StorageType ?? '',
                  deliveryName: legacyOrder.DeliveryName ?? '',
                  addressLine1: legacyOrder.DeliveryStreet1 ?? '',
                  city: legacyOrder.DeliveryCity ?? '',
                  provinceCaption:
                    legacyOrder.DeliveryStateProvinceCaption ?? '',
                  postalCode: legacyOrder.DeliveryPostalCode ?? '',
                })}
              </LegacyOrderHistoryRowOrdeDestination>
            </LegacyOrderHistoryHeaderDataHalfContainer>
          )}
      </LegacyOrderHistoryRowDelivery>
      <>
        {legacyOrder.OrderItems.map((order) => (
          <LegacyOrderHistoryRowOrderItem key={order.ProductCode}>
            <StatusContainer>
              <ProductImage
                productCode={order.ProductCode}
                width={24}
                height={24}
                marginRight={8}
              />
              <StatusLabel>
                {order.Quantity} x {order.ProductCaption}
              </StatusLabel>
            </StatusContainer>
          </LegacyOrderHistoryRowOrderItem>
        ))}
      </>
      <LegacyOrderHistoryRowFooter>
        <Button
          onClick={() => setOpenOrderDetails(true)}
          size="sm"
          variant="primary"
        >
          {t('account.legacyOrderHistory.viewDetails')}
        </Button>
      </LegacyOrderHistoryRowFooter>
      <LegacyOrderDetailsModal
        isOpen={openOrderDetails}
        handleClose={() => setOpenOrderDetails(false)}
        legacyOrder={legacyOrder}
      />
    </LegacyOrderHistoryRowContainer>
  );
};

LegacyOrderHistoryRow.propTypes = {
  legacyOrder: PropTypes.shape({
    CreatedOn: PropTypes.string,
    OrderCode: PropTypes.string,
    OrderTotal: PropTypes.string,
    OrderType: PropTypes.string,
    OrderStateType: PropTypes.string,
    StorageLocation: PropTypes.string,
    DeliveryStreet1: PropTypes.string,
    OrderItems: PropTypes.arrayOf(
      PropTypes.shape({
        ProductCode: PropTypes.string,
        Quantity: PropTypes.number,
        ProductCaption: PropTypes.string,
      }),
    ),
    DeliveryName: PropTypes.string,
    DeliveryCity: PropTypes.string,
    DeliveryStateProvinceCaption: PropTypes.string,
    DeliveryStateProvinceCode: PropTypes.string,
    DeliveryPostalCode: PropTypes.string,
    Total: PropTypes.number,
    StorageType: PropTypes.string,
    VaultShipping: PropTypes.number,
    WireFee: PropTypes.number,
    ProductCode: PropTypes.string,
  }),
};

export default LegacyOrderHistoryRow;
